"use client";
import { useState, useRef, useEffect } from "react";
import { motion } from "motion/react";
import { useRouter } from "next/navigation";
import { 
  FileText, 
  Book, 
  User, 
  MessageSquare, 
  Shield, 
  AlertTriangle, 
  Edit, 
  Mail,
  ChevronRight,
  Check,
  Info,
  Clock,
  Globe,
  X
} from "lucide-react";
import { Separator } from "@/components/ui/separator";
import Link from "next/link";

export default function TermsPage() {
  const [activeSection, setActiveSection] = useState<string | null>("agreement");
  const [isMobile, setIsMobile] = useState(false);
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
  const observerRef = useRef<IntersectionObserver | null>(null);
  const isScrollingRef = useRef(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const router = useRouter();

  // Cleanup function to be used in multiple places
  const cleanupAllEffects = () => {
    // Clean up observer
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    
    // Clear any pending timeouts
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    // Reset any ongoing scroll state
    isScrollingRef.current = false;
    
    // Remove any inline styles that might have been added to the html/body
    if (typeof document !== 'undefined') {
      document.documentElement.style.removeProperty('scroll-behavior');
      document.body.style.removeProperty('overflow');
    }
  };

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    
    window.addEventListener("resize", checkMobile);
    
    // Call cleanup when component unmounts
    return () => {
      window.removeEventListener("resize", checkMobile);
      cleanupAllEffects();
    };
  }, []);

  // Listen for route changes to clean up
  useEffect(() => {
    // This will run when the component mounts
    return () => {
      // This will run when the component unmounts (route change)
      cleanupAllEffects();
    };
  }, [router]);

  // Setup intersection observer to detect active section on scroll
  useEffect(() => {
    // Clean up any existing observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    const options = {
      root: null,
      rootMargin: "-100px 0px -40% 0px",
      threshold: 0.2
    };

    observerRef.current = new IntersectionObserver((entries) => {
      // Don't update active section if user is currently clicking to scroll
      if (isScrollingRef.current) return;

      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    }, options);

    // Observe all section elements
    Object.values(sectionRefs.current).forEach((ref) => {
      if (ref) observerRef.current?.observe(ref);
    });

    // Cleanup on unmount or deps change
    return () => {
      cleanupAllEffects();
    };
  }, []);

  const scrollToSection = (id: string) => {
    setActiveSection(id);
    const element = sectionRefs.current[id];
    if (!element) return;

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set a flag to prevent intersection observer from changing active section during programmatic scrolling
    isScrollingRef.current = true;

    // Use a more contained approach for scrolling to minimize global impact
    try {
      const yOffset = -80;
      const y = element.getBoundingClientRect().top + window.scrollY + yOffset;
      
      // Scroll only the specific element into view
      window.scrollTo({
        top: y,
        behavior: "smooth"
      });
    } catch (err) {
      console.error("Error scrolling to section:", err);
    }

    // Reset the flag after animation completes and store the timeout ref
    timeoutRef.current = setTimeout(() => {
      isScrollingRef.current = false;
      timeoutRef.current = null;
    }, 1000);
  };

  const sections = [
    {
      id: "introduction",
      title: "Introduction",
      icon: Book,
      color: "bg-linear-to-r from-blue-500 to-indigo-600",
      content: (
        <div className="backdrop-blur-sm bg-white/80 rounded-xl p-5 shadow-sm border border-blue-100">
          <div className="flex items-start gap-3 mb-4">
            <div className="p-2 rounded-lg bg-blue-100 text-blue-700 mt-0.5">
              <Book className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-xl font-medium text-gray-900 mb-2">Welcome to RentWise</h3>
              <p className="text-gray-700 mb-4">
                These Terms of Service ("Terms") govern your use of the RentWise website and services operated by RentWise Ltd. By accessing or using our service, you agree to be bound by these Terms.
              </p>
              <p className="text-gray-700">
                Please read these Terms carefully before using our service. If you disagree with any part of these terms, then you may not access the service.
              </p>
            </div>
          </div>
          
          <div className="bg-linear-to-br from-blue-50 to-indigo-50 p-4 rounded-lg">
            <h4 className="font-medium text-blue-800 mb-2">Quick Overview</h4>
            <ul className="text-gray-700 text-sm space-y-1">
              <li>• Use our service responsibly and legally</li>
              <li>• Respect other users and property owners</li>
              <li>• Provide accurate information in your listings and reviews</li>
              <li>• Report any issues or violations to our support team</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: "acceptance",
      title: "Acceptance of Terms",
      icon: Shield,
      color: "bg-linear-to-r from-purple-500 to-pink-600",
      content: (
        <div className="backdrop-blur-sm bg-white/80 rounded-xl p-5 shadow-sm border border-purple-100">
          <div className="flex items-start gap-3 mb-4">
            <div className="p-2 rounded-lg bg-purple-100 text-purple-700 mt-0.5">
              <Shield className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-xl font-medium text-gray-900 mb-2">Agreement to Terms</h3>
              <p className="text-gray-700 mb-4">
                By accessing and using this website, you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
              </p>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="bg-linear-to-br from-purple-50 to-pink-50 p-4 rounded-lg">
              <h4 className="font-medium text-purple-800 mb-2">What Acceptance Means</h4>
              <ul className="text-gray-700 text-sm space-y-2">
                <li>• You are legally bound by these terms</li>
                <li>• You confirm you are authorized to enter into this agreement</li>
                <li>• You acknowledge reading and understanding all provisions</li>
                <li>• You agree to comply with applicable laws and regulations</li>
              </ul>
            </div>
            
            <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-amber-800 mb-1">Important Note</h4>
                  <p className="text-amber-700 text-sm">
                    These terms may be updated from time to time. Continued use of the service after changes constitutes acceptance of the new terms.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: "user-accounts",
      title: "User Accounts",
      icon: User,
      color: "bg-linear-to-r from-emerald-500 to-teal-600",
      content: (
        <div className="space-y-6">
          <div className="backdrop-blur-sm bg-white/80 rounded-xl p-5 shadow-sm border border-emerald-100">
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2 rounded-lg bg-emerald-100 text-emerald-700 mt-0.5">
                <User className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-xl font-medium text-gray-900 mb-2">Account Requirements</h3>
                <p className="text-gray-700">
                  When you create an account with us, you must provide information that is accurate, complete, and current at all times. You are responsible for safeguarding the password and for all activities that occur under your account.
                </p>
              </div>
            </div>
          </div>
          
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              {
                title: "Account Security",
                items: [
                  "Use a strong, unique password",
                  "Don't share your login credentials",
                  "Log out from shared devices",
                  "Enable two-factor authentication if available"
                ]
              },
              {
                title: "Account Information",
                items: [
                  "Provide accurate personal information",
                  "Keep your contact details updated",
                  "Verify your email address",
                  "Complete your profile for better experience"
                ]
              }
            ].map((section, index) => (
              <div key={index} className="bg-linear-to-br from-emerald-50 to-teal-50 p-4 rounded-lg">
                <h4 className="font-medium text-emerald-800 mb-3">{section.title}</h4>
                <ul className="text-gray-700 text-sm space-y-1">
                  {section.items.map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )
    },
    {
      id: "user-conduct",
      title: "User Conduct",
      icon: Edit,
      color: "bg-linear-to-r from-amber-500 to-orange-600",
      content: (
        <div className="space-y-6">
          <div className="backdrop-blur-sm bg-white/80 rounded-xl p-5 shadow-sm border border-amber-100">
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2 rounded-lg bg-amber-100 text-amber-700 mt-0.5">
                <Edit className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-xl font-medium text-gray-900 mb-2">Community Guidelines</h3>
                <p className="text-gray-700">
                  RentWise is a community platform. We expect all users to treat others with respect and to use our service responsibly.
                </p>
              </div>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-medium text-green-800 mb-3 flex items-center gap-2">
                  <Check className="w-5 h-5" />
                  Encouraged Behavior
                </h4>
                <ul className="text-green-700 text-sm space-y-1">
                  <li>• Provide honest and accurate reviews</li>
                  <li>• Communicate respectfully with other users</li>
                  <li>• Report inappropriate content or behavior</li>
                  <li>• Follow local laws and regulations</li>
                </ul>
              </div>
              
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-medium text-red-800 mb-3 flex items-center gap-2">
                  <X className="w-5 h-5" />
                  Prohibited Activities
                </h4>
                <ul className="text-red-700 text-sm space-y-1">
                  <li>• Posting false or misleading information</li>
                  <li>• Harassment or discriminatory behavior</li>
                  <li>• Spam or unsolicited communications</li>
                  <li>• Violating intellectual property rights</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: "liability",
      title: "Limitation of Liability",
      icon: AlertTriangle,
      color: "bg-linear-to-r from-red-500 to-pink-600",
      content: (
        <div className="backdrop-blur-sm bg-white/80 rounded-xl p-6 shadow-sm border border-red-100">
          <div className="flex items-start gap-3 mb-4">
            <div className="p-2 rounded-lg bg-red-100 text-red-700 mt-0.5">
              <AlertTriangle className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-xl font-medium text-gray-900 mb-2">Important Legal Notice</h3>
              <p className="text-gray-700 mb-4">
                In no event shall RentWise, nor its directors, employees, partners, agents, suppliers, or affiliates, be liable for any indirect, incidental, special, consequential, or punitive damages.
              </p>
            </div>
          </div>
          
          <div className="bg-linear-to-br from-red-50 to-pink-50 p-4 rounded-lg">
            <h4 className="font-medium text-red-800 mb-2">What This Means</h4>
            <ul className="text-gray-700 text-sm space-y-2">
              <li>• We provide our service "as is" without warranties</li>
              <li>• Users are responsible for verifying property information</li>
              <li>• We facilitate connections but don't guarantee outcomes</li>
              <li>• Always conduct your own due diligence before making decisions</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: "privacy",
      title: "Privacy & Data Protection",
      icon: Shield,
      color: "bg-linear-to-r from-indigo-500 to-purple-600",
      content: (
        <div className="backdrop-blur-sm bg-white/80 rounded-xl p-6 shadow-sm border border-indigo-100">
          <div className="flex items-start gap-3 mb-4">
            <div className="p-2 rounded-lg bg-indigo-100 text-indigo-700 mt-0.5">
              <Shield className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-xl font-medium text-gray-900 mb-2">Your Privacy Matters</h3>
              <p className="text-gray-700 mb-4">
                Your privacy is important to us. Our Privacy Policy explains how we collect, use, and protect your information when you use our service.
              </p>
            </div>
          </div>
          
          <div className="bg-linear-to-br from-indigo-50 to-purple-50 p-4 rounded-lg">
            <h4 className="font-medium text-indigo-800 mb-2">Data Protection Highlights</h4>
            <ul className="text-gray-700 text-sm space-y-2">
              <li>• We collect only necessary information to provide our service</li>
              <li>• Your data is encrypted and securely stored</li>
              <li>• We don't sell your personal information to third parties</li>
              <li>• You can request access, correction, or deletion of your data</li>
            </ul>
            <div className="mt-3 pt-3 border-t border-indigo-200">
              <p className="text-sm text-indigo-700">
                For detailed information, please review our <Link href="/privacy" className="underline hover:no-underline">Privacy Policy</Link>.
              </p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: "changes",
      title: "Changes to Terms",
      icon: Edit,
      color: "bg-linear-to-r from-cyan-500 to-blue-600",
      content: (
        <div className="backdrop-blur-sm bg-white/80 rounded-xl p-6 shadow-sm border border-cyan-100">
          <div className="flex items-start gap-3 mb-4">
            <div className="p-2 rounded-lg bg-cyan-100 text-cyan-700 mt-0.5">
              <Edit className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-xl font-medium text-gray-900 mb-2">Updates to These Terms</h3>
              <p className="text-gray-700">
                We reserve the right to modify or replace these Terms at any time. If a revision is material, we will try to provide at least 30 days notice prior to any new terms taking effect.
              </p>
            </div>
          </div>
          
          <div className="bg-linear-to-br from-cyan-50 to-blue-50 p-4 rounded-lg">
            <h4 className="font-medium text-cyan-800 mb-2">How You'll Be Notified</h4>
            <ul className="text-gray-700 text-sm space-y-2">
              <li>• Email notification to registered users</li>
              <li>• Notice banner on our website</li>
              <li>• Updated "Last Modified" date on this page</li>
              <li>• Continued use constitutes acceptance of new terms</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: "contact",
      title: "Contact Information",
      icon: Mail,
      color: "bg-linear-to-r from-violet-500 to-fuchsia-600",
      content: (
        <div>
          <p className="mb-5 text-gray-700">
            If you have any questions about these Terms of Service, please contact us:
          </p>
          <div className="bg-linear-to-br from-violet-50 to-fuchsia-50 rounded-xl overflow-hidden shadow-sm">
            <div className="p-5 border-b border-violet-100">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-full bg-violet-100 text-violet-700">
                  <Mail className="w-5 h-5" />
                </div>
                <h3 className="text-xl font-medium text-gray-900">Email Support</h3>
              </div>
              <p className="text-violet-700 font-medium">legal@rentwise.com</p>
            </div>
            <div className="p-5 border-b border-fuchsia-100">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-full bg-fuchsia-100 text-fuchsia-700">
                  <Globe className="w-5 h-5" />
                </div>
                <h3 className="text-xl font-medium text-gray-900">Mailing Address</h3>
              </div>
              <p className="text-gray-700">RentWise Legal Department<br />123 Legal Street<br />Law City, LC 12345</p>
            </div>
            <div className="p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-full bg-purple-100 text-purple-700">
                  <Clock className="w-5 h-5" />
                </div>
                <h3 className="text-xl font-medium text-gray-900">Response Time</h3>
              </div>
              <p className="text-gray-700">We aim to respond to all legal inquiries within 5-7 business days.</p>
            </div>
          </div>
        </div>
      )
    }
  ];

  return (
    <div id="terms-page" className="min-h-screen relative bg-linear-to-b from-gray-50 to-white overflow-y-auto overflow-x-hidden">
      {/* Background decorative elements */}
      <div className="absolute top-20 left-0 w-64 h-64 rounded-full bg-blue-100/30 blur-3xl -z-10" />
      <div className="absolute bottom-20 right-10 w-80 h-80 rounded-full bg-indigo-100/20 blur-3xl -z-10" />
      <div className="absolute top-1/3 right-0 w-72 h-72 rounded-full bg-purple-100/30 blur-3xl -z-10" />
      
      {/* Header */}
      <header className="pt-12 pb-6 md:pt-20 md:pb-12 container mx-auto px-4 md:px-6">
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center max-w-3xl mx-auto"
        >
          <motion.div 
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
                                transition={{ delay: 0.2, type: "spring" as const, stiffness: 100 }}
            className="mb-6 inline-flex"
          >
            <div className="relative">
              <div className="absolute inset-0 rounded-full bg-blue-300 animate-ping opacity-20"></div>
              <div className="relative p-4 rounded-full bg-linear-to-r from-blue-500 to-indigo-600 shadow-lg">
                <FileText className="w-7 h-7 md:w-8 md:h-8 text-white" />
              </div>
            </div>
          </motion.div>

          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.7 }}
            className="text-4xl md:text-5xl font-bold bg-linear-to-r from-blue-600 to-indigo-600 text-transparent bg-clip-text mb-4"
          >
            Terms of Service
          </motion.h1>

          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.7 }}
            className="text-gray-600"
          >
            Last updated: April 23, 2025
          </motion.p>
        </motion.div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 md:px-6 pb-20">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Sidebar Navigation for Desktop */}
          <motion.aside 
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6, duration: 0.7 }}
            className="hidden md:block md:w-64 lg:w-72 shrink-0"
          >
            <div className="sticky top-8">
              <nav className="space-y-1 backdrop-blur-md bg-white/70 rounded-2xl p-3 shadow-sm border border-gray-100">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all ${
                      activeSection === section.id 
                        ? `${section.color} text-white shadow-sm` 
                        : "hover:bg-gray-50 text-gray-700"
                    }`}
                  >
                    <span className={`p-1.5 rounded-md ${activeSection === section.id ? "bg-white/20" : "bg-gray-100"}`}>
                      <section.icon className="w-5 h-5" />
                    </span>
                    <span className="font-medium">{section.title}</span>
                  </button>
                ))}
              </nav>

              <div className="mt-6 bg-linear-to-br from-blue-50 to-indigo-50 p-5 rounded-xl border border-blue-100">
                <h3 className="font-medium text-gray-900 mb-2">Have Questions?</h3>
                <p className="text-sm text-gray-700 mb-3">
                  If you have any questions about our terms or legal policies, our team is ready to assist.
                </p>
                <button className="text-sm bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors w-full">
                  Contact Legal Team
                </button>
              </div>
            </div>
          </motion.aside>

          {/* Mobile Navigation - Pills */}
          <div className="md:hidden sticky top-0 z-10 pt-3 pb-4 bg-gray-50/80 backdrop-blur-sm">
            <div className="overflow-x-auto flex gap-2 p-1 snap-x">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-full whitespace-nowrap shrink-0 transition-colors snap-start ${
                    activeSection === section.id 
                      ? `${section.color} text-white` 
                      : "bg-white text-gray-800 shadow-sm"
                  }`}
                >
                  <section.icon className="w-5 h-5" />
                  <span className="text-sm font-medium">{section.title}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="grow max-w-3xl">
            {sections.map((section) => (
              <motion.section
                key={section.id}
                id={section.id}
                ref={(el) => { if (el) { sectionRefs.current[section.id] = el; } }}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="mb-16 scroll-mt-20"
              >
                <div className={`relative ${section.color} text-white py-4 px-6 rounded-xl shadow-md mb-6`}>
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                      <section.icon className="w-5 h-5" />
                    </div>
                    <h2 className="text-2xl font-bold">{section.title}</h2>
                  </div>
                </div>
                
                <div className="px-2">
                  {section.content}
                </div>

                {section.id !== sections[sections.length - 1].id && (
                  <div className="flex justify-end mt-8">
                    <button 
                      onClick={() => scrollToSection(sections[sections.findIndex(s => s.id === section.id) + 1].id)}
                      className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                    >
                      <span>Next section</span>
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </motion.section>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
} 